import React, { useState } from 'react';
import { Apiary } from '../../types';
import { hiveService } from '../../services/firebase';

interface QuickAddHiveProps {
  selectedApiary: Apiary;
  onHiveAdded: () => void;
  onCancel: () => void;
}

export const QuickAddHive: React.FC<QuickAddHiveProps> = ({
  selectedApiary,
  onHiveAdded,
  onCancel
}) => {
  const [formData, setFormData] = useState({
    name: '',
    queen_origin: '',
    queen_marked: false,
    queen_marking_color: '' as 'white' | 'yellow' | 'red' | 'green' | 'blue' | '',
    established: new Date().toISOString().split('T')[0]
  });
  const [saving, setSaving] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    setSaving(true);
    try {
      await hiveService.addHive({
        name: formData.name.trim(),
        queen_origin: formData.queen_origin.trim() || undefined,
        queen_marked: formData.queen_marked,
        queen_marking_color: formData.queen_marked && formData.queen_marking_color ? formData.queen_marking_color : undefined,
        established: formData.established,
        apiary_id: selectedApiary.id
      });

      onHiveAdded();
    } catch (error) {
      console.error('Error adding hive:', error);
      alert('Failed to add hive. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ 
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1001
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        maxWidth: '500px',
        width: '90%'
      }}>
        <h3 style={{ marginTop: 0 }}>
          ➕ Add New Hive
        </h3>
        <p style={{ color: '#666', marginBottom: '20px' }}>
          Apiary: <strong>{selectedApiary.name}</strong>
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Hive Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., Hive 1, Queen Anne, etc."
              required
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Queen Origin
            </label>
            <input
              type="text"
              name="queen_origin"
              value={formData.queen_origin}
              onChange={handleInputChange}
              placeholder="e.g., Local, Buckfast, Carniolan, etc. (optional)"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="queen_marked"
                checked={formData.queen_marked}
                onChange={handleInputChange}
                style={{ marginRight: '8px' }}
              />
              👑 Queen Marked
            </label>
            <small style={{ color: '#666', fontSize: '12px' }}>Check if your queen has been marked with colour paint</small>
          </div>

          {formData.queen_marked && (
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Queen Marking Color
              </label>
              <select
                name="queen_marking_color"
                value={formData.queen_marking_color}
                onChange={handleInputChange}
                required={formData.queen_marked}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="">Select color based on year</option>
                <option value="white">⚪ White (2026, 2021, 2016...)</option>
                <option value="yellow">🟡 Yellow (2027, 2022, 2017...)</option>
                <option value="red">🔴 Red (2023, 2018, 2013...)</option>
                <option value="green">🟢 Green (2024, 2019, 2014...)</option>
                <option value="blue">🔵 Blue (2025, 2020, 2015...)</option>
              </select>
              <small style={{ color: '#666', fontSize: '12px' }}>2025 = Blue, 2024 = Green, 2023 = Red, 2022 = Yellow, 2021 = White</small>
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Established Date
            </label>
            <input
              type="date"
              name="established"
              value={formData.established}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button 
              type="button"
              onClick={onCancel}
              disabled={saving}
              style={{
                padding: '10px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            <button 
              type="submit"
              disabled={saving || !formData.name.trim()}
              style={{
                padding: '10px 16px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: (saving || !formData.name.trim()) ? 'not-allowed' : 'pointer',
                opacity: (saving || !formData.name.trim()) ? 0.6 : 1
              }}
            >
              {saving ? 'Adding...' : 'Add Hive'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
